export const meta = {
  id: '13-animals',
  order: 13,
  titleFi: 'Eläimet',
  titleEn: 'Animals',
  duolingoUnit: 'duo-14-animals',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['jaos', 'atroksia', 'zokla', 'bianor', 'kēlio', 'klios', 'qryldes', 'vōljes', 'bēgor', 'uēs', 'ūlio', 'ours:jaohītsos', 'ours:tūghrenka', 'ours:ēnguragon', 'ours:ellalion', 'ours:biādrītsos', 'ours:pamītsos', 'ours:tīkoqitta', 'ours:deklinon', 'ours:kōttenon', 'ours:hyliapenka', 'ours:dēdetir', 'ours:mīvarrijion', 'ours:dȳñurlion', 'ours:bantenka', 'ours:yrsparys', 'ours:tēmītsos', 'ours:tyvītsos', 'ours:papuga', 'ours:ñovio'],
  exampleIds: ['ex-3c8ae2d99149', 'ex-abc65cc78d13', 'ex-c231b78e6657', 'ex-7492650d0c51', 'ex-a385e0134064', 'ex-3569d0454f07', 'ex-70853508a533', 'ex-8b94886b97c2', 'ex-51b07f61553a', 'ex-fb9c0e7de2e8', 'ex-809b34b2bbbb'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Animals',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Demonstratives_1',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Declensions',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Phonotactics',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['genders', 'declensions'],
  pronunciation: { focus: 'long-vowels', words: ['kēlio', 'vōljes', 'bēgor', 'uēs', 'ūlio'] },
  letter: {
    fi: 'Kirjoita lempieläimestäsi: mikä se on, millainen se on ja mitä se tekee.',
    en: 'Write about your favourite animal: what it is, what it is like and what it does.',
  },
}

# Eläimet

Tulen ja jään maailmassa eläimet ovat sukujen tunnuksia. Duolingon Tips & Notes -ohjeissa annetaan esimerkkejä: susi kuuluu Starkeille, leijona Lannistereille, kala Tullyille ja lohikäärme Targaryeneille. Eläimen nimi voi usein tarkoittaa koko sukua tai sen päämiestä. Kun valyriankielisessä lauseessa susi käy leijonan kimppuun, sen voi lukea myös sukujen välisenä riitana:

<Example id="ex-3c8ae2d99149" />

<Interlinear hv="Zokl|a kēl|io kostōbirī angotas." gloss="susi-NOM leijona-ACC voimakkaasti purra-PRF.3SG" fi="Susi puri leijonaa voimakkaasti." />

<Hv glossed>Kēlio</Hv> ’leijona’ kuuluu kolmanteen <Term id="deklinaatio">taivutusluokkaan</Term>, jossa nominatiivi ja akkusatiivi ovat samat. Kumpi on kumpi, näkyy siis sanasta <Hv>zokla</Hv>. Se on nominatiivissa, joten susi on se, joka puree. Jos leijona purisi sutta, susi olisi akkusatiivissa: <Hv>zokle</Hv>.

Tämän oppitunnin sanoissa on kaksi kalaa, <Hv glossed>klios</Hv> ’kala’ ja <Hv glossed>bēgor</Hv> ’taimen’:

<Example id="ex-abc65cc78d13" />

## Suku: taipumus, ei sääntö

Wikin sukusivun mukaan eläinten suvuissa on selvä taipumus. Päiväeläimet ovat usein aurinkosukua ja yöeläimet kuusukua. Tämän oppitunnin sanoista <Hv glossed>zokla</Hv> ’susi’ ja <Hv glossed>atroksia</Hv> ’pöllö’ ovat kuusukua, ja <Hv glossed>jaos</Hv> ’koira’, <Hv glossed>qryldes</Hv> ’karju’ ja <Hv glossed>vōljes</Hv> ’korppi’ ovat aurinkosukua. <Hv glossed>Atroksia</Hv> tarkoittaa muuten myös ’yö’:

<Example id="ex-c231b78e6657" />

Taipumus ei kuitenkaan ole sääntö. Haukka <Hv>ūlio</Hv> on päivälintu mutta kuusukua. Vedessä elävät eläimet eivät nekään ole automaattisesti vesisukua: <Hv glossed>bēgor</Hv> ’taimen’ on, mutta <Hv glossed>klios</Hv> ’kala’ ja <Hv glossed>uēs</Hv> ’kalmari’ ovat aurinkosukua. Lisäksi <Hv glossed>bianor</Hv> ’uuhi’ on vesisukua, vaikka lammas ei ole vesieläin. Suomessa ei ole sukuja, joten tässä ei auta muu kuin opetella suku sanan mukana.

### Onko eläin elollinen?

Kysymyssanojen ja osoittavien pronominien elollinen–eloton-jako ei seuraa sukua, ja eläinten kohdalla se jää puhujan päätettäväksi. Osoittavien pronominien kohdalla Duolingon ohjeissa sanotaan, että jokainen puhuja päättää itse, ovatko eläimet tarpeeksi elollisia B-luokan pronominille. Wikin pronominisivu vertaa tätä siihen, että englannissa eläimestä voi sanoa *he/she* tai *it*. Esimerkeistä löytyvät molemmat valinnat:

<Example id="ex-7492650d0c51" />

<Example id="ex-a385e0134064" />

Ensimmäisessä lintu saa elottoman muodon <Hv>konys</Hv>, ja jälkimmäisessä haukasta kysytään elollisella sanalla <Hv>spare</Hv>.

## Erikoiset taivutukset

Duolingon ohjeet kehottavat katsomaan tämän oppitunnin sanoja tarkasti, koska osa niistä edustaa valyrian harvinaisempia <Term id="deklinaatio">taivutusluokkia</Term>. Käydään ne läpi yksi kerrallaan.

### Supistuvat: jaos, klios, uēs

<Paradigm lemma="jaos" cases="nom,acc,gen" numbers="sg,pl" />

Nämä kolme taipuvat samalla tavalla kuin perheen oppitunnin <Hv>rūs</Hv>. Wikin taivutussivulla tyyppi on aurinkosuvun supistuva h-vartalo. Lyhyt muoto on yksikön nominatiivi, akkusatiivi ja vokatiivi. Useimmissa muissa muodoissa vartaloon tulee h: <Hv>jaos</Hv> → <Hv>jaoho</Hv>, <Hv>jaohossa</Hv>, <Hv>klios</Hv> → <Hv>kliho</Hv>, <Hv>klihossa</Hv> ja <Hv>uēs</Hv> → <Hv>uēho</Hv>, <Hv>uēhossa</Hv>.

<Pronounce word="uēs" />

<Example id="ex-3569d0454f07" />

<Example id="ex-70853508a533" />

Jälkimmäisessä on myös kertausta: kyllä–ei-kysymys ilman kysymysliitettä.

### Korppi ja lj-äänne: vōljes

<Paradigm lemma="vōljes" cases="nom,acc,gen" numbers="sg,pl" />

<Pronounce word="vōljes" />

Kirjainyhdistelmä lj on yksi äänne, liudentunut l (IPA [ʎ]). Wikin äännerakennesivun mukaan se menettää liudennuksensa i:n edellä, jolloin lj muuttuu tavalliseksi l:ksi. Siksi akkusatiivi on <Hv>vōlī</Hv> eikä *vōljī*. Muissa muodoissa lj säilyy: <Hv>vōljesse</Hv>, <Hv>vōljo</Hv>.

<Example id="ex-8b94886b97c2" />

<Interlinear hv="Vōlj|esse ēdru|si." gloss="korppi-NOM.PL nukkua-PRS.3PL" fi="Korpit nukkuvat." />

<Example id="ex-51b07f61553a" />

### Uuhi ja nasaalisääntö: bianor

<Paradigm lemma="bianor" cases="nom,acc,gen" numbers="sg,pl" />

<Pronounce word="bianor" />

Tämä on tuttu sääntö omistussanojen oppitunnilta. Kun n joutuu r:n eteen, n katoaa, vokaali pitenee ja tilalle tulee d: <Hv>bianor</Hv> → <Hv>biādra</Hv>, <Hv>biādro</Hv>, <Hv>biādroti</Hv>. Seuraavassa lauseessa sama sääntö näkyy kahdesti peräkkäin, koska <Hv glossed>kōdra</Hv> ’nuo’ on syntynyt samalla tavalla. Lause vahvistaa samalla, että <Hv glossed>kōdra</Hv> tarkoittaa ’nuo’ eikä ’nämä’:

<Example id="ex-fb9c0e7de2e8" />

<Example id="ex-809b34b2bbbb" />

### Muut

- <Hv>zokla</Hv> taipuu kuin <Hv>vala</Hv>: <Hv>zokle</Hv>, <Hv>zoklo</Hv>, <Hv>zokli</Hv>.
- <Hv>atroksia</Hv> kuuluu -ia-sanojen alatyyppiin: akkusatiivi <Hv>atroksie</Hv>, genetiivi <Hv>atroksio</Hv>, monikko <Hv>atroksī</Hv> ja monikon genetiivi <Hv>atroksȳti</Hv>.
- <Hv>ūlio</Hv> kuuluu -io-sanojen alatyyppiin: monikko <Hv>ūlia</Hv> ja genetiivi <Hv>ūliō</Hv>. Tämä on genetiivin oppitunnilla luvattu pitkä -ō.
- <Hv>qryldes</Hv> taipuu kuin <Hv>zaldrīzes</Hv>: monikko <Hv>qryldesse</Hv>, akkusatiivi <Hv>qryldī</Hv>.
- <Hv>bēgor</Hv> taipuu kuin <Hv>qȳbor</Hv>: monikko <Hv>bēgra</Hv>, genetiivi <Hv>bēgro</Hv>.

<Pronounce word="atroksia" />

<Pronounce word="qryldes" />

<Practice hv="Zokla jaos urnes." fi="Susi näkee koiran." />

<Practice hv="Zokle jaos urnes." fi="Koira näkee suden." />

<Practice hv="Jaohossa ēdrusi." fi="Koirat nukkuvat." />

<Practice hv="Ñuhys jaos ēdrus." fi="Koirani nukkuu." />

Kaksi ensimmäistä harjoituslausetta eroavat vain yhden vokaalin verran, mutta niiden merkitys on päinvastainen. <Hv>Jaos</Hv> on sama subjektina ja objektina, joten roolin näkee muodosta <Hv>zokla</Hv> tai <Hv>zokle</Hv>.

## Lopuksi

Eläinten suku on taipumus eikä sääntö, ja se, onko eläin elollinen vai eloton, on puhujan valinta. Taivutuksissa uutta olivat supistuvat h-vartalot, lj:n muuttuminen l:ksi i:n edellä ja jo tuttu nasaali + r. Seuraavalla oppitunnilla siirrytään verbeihin ja käydään läpi koko preesens.

<Vocab />

<Check />
